import { extract, noop, watch } from '@vpap_svelte/internal';
export class ResponseError extends Error {
    #response;
    constructor(response, message) {
        super(message);
        this.#response = response;
    }
    get response() {
        return this.#response;
    }
}
ResponseError.prototype.name = 'ResponseError';
async function runFetch(url, requestInit, callbacks) {
    try {
        const response = await fetch(url, requestInit);
        if (response.ok) {
            callbacks.onSuccess(response);
        }
        else {
            throw new ResponseError(response, 'Error fetching resource');
        }
    }
    catch (e) {
        callbacks.onError(e);
    }
    finally {
        callbacks.onCompletion();
    }
}
/**
 * Run a network request reactively in response to potential changes in variables
 *
 * @param resource The resource where the network request should go
 * @param options Request options (@see https://developer.mozilla.org/en-US/docs/Web/API/RequestInit)
 * @param useFetchOptions Options for how you want to handle the request. These are mostly callbacks.
 * @returns Handlers for controlling network requests
 */
export function useFetch(resource, options = {}, useFetchOptions = {}) {
    const fetchOptions = {
        onFetch: useFetchOptions.onFetch ?? noop,
        onError: useFetchOptions.onError ?? noop,
        onSuccess: useFetchOptions.onSuccess ?? noop,
        onCompletion: useFetchOptions.onCompletion ?? noop,
        immediate: useFetchOptions.immediate ?? true,
    };
    let isActive = $state(fetchOptions.immediate);
    let isFetching = $state(false);
    let controller = new AbortController();
    const url = $derived(extract(resource));
    const baseRequestInit = $derived(extract(options));
    const stop = $effect.root(() => {
        watch([() => url, () => baseRequestInit, () => isActive], () => {
            if (!isActive)
                return;
            const signal = controller.signal;
            const requestInit = {
                ...baseRequestInit,
                signal,
            };
            isFetching = true;
            fetchOptions.onFetch();
            runFetch(url, requestInit, {
                onSuccess: fetchOptions.onSuccess,
                onError: fetchOptions.onError,
                onFetch: fetchOptions.onFetch,
                onCompletion: () => {
                    if (fetchOptions.immediate === false) {
                        // on completion, we want to reset the value of `isActive` to its initial value
                        // so that running `run` and changing the URL or RequestInit
                        // options only triggers an effect when immediate is true
                        isActive = false;
                    }
                    isFetching = false;
                    fetchOptions.onCompletion();
                },
            });
            return () => {
                if (isFetching) {
                    controller.abort();
                    controller = new AbortController();
                }
            };
        });
    });
    $effect(() => {
        return stop;
    });
    return {
        /**
         * Whether or not the effect is currently active (or whether it's paused). (Note that this is different
         * from whether or not it is currently running)
         */
        get isActive() {
            return isActive;
        },
        /**
         * Whether or not the effect is currently running (fetching)
         */
        get isFetching() {
            return isFetching;
        },
        /**
         * Permanently cancel the effect
         */
        stop,
        /**
         * Pause the effect, aborting the request if it's currently running
         */
        pause() {
            isActive = false;
        },
        /**
         * Resume the effect, triggering it to run.
         */
        run() {
            isActive = true;
        },
        /**
         * Aborts a request. This allows people to abort requests early (for instance, if a timeout has been exceeded)
         */
        abort() {
            controller.abort();
            controller = new AbortController();
        },
    };
}
